import { useAtomValue, useSetAtom } from 'jotai';
import { motion } from 'motion/react';
import { useEffect } from 'react';
import { DialogTrigger } from 'react-aria-components';
import { useParams } from 'react-router-dom';

import { EKHO_SERVICES_PROVIDER_ID } from '@ekhodealer/ekho-common';
import {
	ActionMenu,
	ActionMenuItem,
	ActionMenuTrigger,
	Button,
	Drawer,
	Group,
	Tabs,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { TabItem } from '@ekhodealer/ekho-common/rev';

import {
	ChevronDown12,
	ChevronDown24,
	ChevronLeft24,
	ChevronUp24,
	SquareUp24,
} from '@common/components/icons';
import ArrowsSync24 from '@common/components/icons/ArrowsSync24';
import ArrowUpRight24 from '@common/components/icons/ArrowUpRight24';
import { NavStack } from '@common/components/NavStack';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';

import { useExportOrders } from '../api/useExportOrders';
import { useSearchOrders } from '../api/useSearchOrders';
import { useSyncOrder } from '../api/useSyncOrder';
import {
	actionItemNavAtom,
	actionItemNavHandlerAtom,
	navigateBackAtom,
	orderDetailsDrawerAtom,
	OrderDetailsTab,
	resetActionItemNavAtom,
} from '../atoms/orderDetailsDrawerAtoms';
import { ordersParamsAtom } from '../atoms/ordersAtoms';
import { useOrderNavigation } from '../hooks/useOrderNavigation';

import { DetailsTabContent } from './order-details/DetailsTabContent';
import { DocumentsTabContent } from './order-documents/DocumentsTabContent';
import { PaymentsTabContent } from './order-payment/PaymentsTabContent';
import { ActionItemDetailView } from './order-progress/ActionItemDetailView';
import { ProgressTabContent } from './order-progress/ProgressTabContent';
import { TimelineTabContent } from './order-timeline/TimelineTabContent';

import '@common/components/_drawerShared.scss';

export const OrderDetailsDrawer = () => {
	// Get URL params
	const { orderId, detailsTab } = useParams<{
		orderId?: string;
		detailsTab?: string;
	}>();
	const {
		closeOrderDetails,
		switchOrderDetailsTab,
		navigateToPreviousOrder,
		navigateToNextOrder,
		getNavigationState,
	} = useOrderNavigation();

	// Drawer is open if orderId and detailsTab are present in URL
	const isDrawerOpen = Boolean(orderId && detailsTab);
	const activeTab = (detailsTab as OrderDetailsTab) || OrderDetailsTab.PROGRESS;

	// Get orders data to find the selected order
	const params = useAtomValue(ordersParamsAtom);
	const { data } = useSearchOrders(params);
	const selectedRow = data?.data.find((order) => order.orderId === orderId) || null;
	const totalCount = data?.count ?? 0;

	// Get navigation state for prev/next buttons
	const navigationState = orderId
		? getNavigationState(orderId, totalCount)
		: { canGoPrevious: false, canGoNext: false };

	// Action item navigation state
	const { viewId, selectedActionItem, currentTitle } = useAtomValue(actionItemNavAtom);
	const currentServicesProviderId = useCurrentServicesProviderId();
	const navHandler = useAtomValue(actionItemNavHandlerAtom);
	const navigateBack = useSetAtom(navigateBackAtom);
	const resetActionItemNav = useSetAtom(resetActionItemNavAtom);

	// Sync selectedRow to atom for tab content components
	const setDrawerState = useSetAtom(orderDetailsDrawerAtom);
	useEffect(() => {
		setDrawerState({ selectedRow });
	}, [selectedRow, setDrawerState]);

	// Export orders mutation
	const exportOrdersMutation = useExportOrders();

	// Sync order mutation
	const syncOrderMutation = useSyncOrder();

	// Close drawer handler
	const closeDrawer = () => {
		// Reset action item navigation when drawer closes
		resetActionItemNav();
		// Navigate back to orders page
		closeOrderDetails();
	};

	// Set active tab handler
	const setActiveTab = (tab: OrderDetailsTab) => {
		if (!orderId) return;
		switchOrderDetailsTab(orderId, tab);
	};

	const handleExportAsCsv = async () => {
		if (!selectedRow) return;

		try {
			await exportOrdersMutation.mutateAsync({
				purchasedVehicleIds: [selectedRow.purchasedVehicleId],
			});

			toast({
				message: 'Order exported',
				variant: 'success',
			});
		} catch {
			toast({
				message: 'Failed to export order. Please try again.',
				variant: 'error',
			});
		}
	};

	const handleSyncOrder = async () => {
		if (!selectedRow?.purchasedVehicleId) return;

		try {
			await syncOrderMutation.mutateAsync({
				purchasedVehicleId: selectedRow.purchasedVehicleId,
			});

			toast({
				message: 'Order synced successfully',
				variant: 'success',
			});
		} catch {
			toast({
				message: 'Failed to sync order. Please try again.',
				variant: 'error',
			});
		}
	};

	const handleViewInLegacyApp = () => {
		if (!selectedRow) return;

		const baseUrl =
			'https://ekhodealer.retool.com/apps/e775940c-8fe6-11ee-978a-37e396552c79/Admin%20Portal/Single%20Vehicle/Single%20vehicle';
		const params = new URLSearchParams({
			placedOrderId: selectedRow.orderId || '',
			purchasedVehicleId: selectedRow.purchasedVehicleId || '',
		});

		const url = `${baseUrl}?${params.toString()}`;
		window.open(url, '_blank');
	};

	const handlePreviousOrder = () => {
		if (orderId && navigationState.canGoPrevious) {
			navigateToPreviousOrder(orderId, activeTab);
		}
	};

	const handleNextOrder = () => {
		if (orderId && navigationState.canGoNext) {
			navigateToNextOrder(orderId, activeTab);
		}
	};

	// Tab items configuration
	const tabItems: TabItem[] = [
		{ id: OrderDetailsTab.PROGRESS, content: 'Progress' },
		{ id: OrderDetailsTab.DETAILS, content: 'Details' },
		{ id: OrderDetailsTab.PAYMENT_AND_FAI, content: 'Payment & F&I' },
		{ id: OrderDetailsTab.DOCUMENTS, content: 'Documents' },
		{ id: OrderDetailsTab.TIMELINE, content: 'Timeline' },
	];

	const handleTabSelect = (item: TabItem) => {
		setActiveTab(item.id as OrderDetailsTab);
	};

	// Render the appropriate content based on the active tab
	const renderTabContent = () => {
		if (!selectedRow) {
			return null;
		}

		switch (activeTab) {
			case OrderDetailsTab.PROGRESS:
				return <ProgressTabContent />;
			case OrderDetailsTab.DETAILS:
				return <DetailsTabContent />;
			case OrderDetailsTab.PAYMENT_AND_FAI:
				return <PaymentsTabContent />;
			case OrderDetailsTab.DOCUMENTS:
				return <DocumentsTabContent />;
			case OrderDetailsTab.TIMELINE:
				return <TimelineTabContent />;
		}
	};

	// Don't render if no order is selected
	if (!selectedRow) {
		return null;
	}

	// Determine drawer title based on navigation state
	const isOnDetailView = viewId === 'action-item-detail';
	const drawerTitle = isOnDetailView ? (
		<Group itemsSpacing='8' itemsAlignY='center'>
			<Button
				className='drawer-back-button--rev'
				variant='ghost'
				size='large'
				aria-label='Back'
				icon={<ChevronLeft24 />}
				onPress={navigateBack}
			/>
			<Text size='heading' weight='semibold'>
				{currentTitle}
			</Text>
		</Group>
	) : (
		`Order #${selectedRow?.orderNumber || orderId || ''}`
	);

	return (
		<DialogTrigger
			isOpen={isDrawerOpen}
			onOpenChange={(open) => {
				if (!open) {
					closeDrawer();
				}
			}}>
			<Drawer
				placement='right'
				hasCloseButton={true}
				isDismissable={true}
				isKeyboardDismissDisabled={false}
				ariaLabel='Order Details'
				className='drawer--custom'>
				<Drawer.Header>{drawerTitle}</Drawer.Header>
				<Drawer.Content padding={{ x: 24, top: 12, bottom: 64 }}>
					<NavStack handler={navHandler}>
						{/* Root view - main drawer content with tabs */}
						<NavStack.View viewId='root'>
							<div>
								<Tabs
									items={tabItems}
									selectedKey={activeTab}
									onSelect={handleTabSelect}
									className='order-details-tabs--custom'
								/>
							</div>
							{renderTabContent()}
						</NavStack.View>

						{/* Action item detail view */}
						<NavStack.View viewId='action-item-detail'>
							{selectedActionItem && (
								<ActionItemDetailView actionItem={selectedActionItem} />
							)}
						</NavStack.View>
					</NavStack>
				</Drawer.Content>

				{/* Footer with navigation buttons — hidden on action item detail view */}
				{!isOnDetailView && (
					<Drawer.Footer>
						<motion.div
							key={String(navHandler.currentViewId)}
							initial={{ opacity: 0, y: 8 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.2, ease: 'easeOut' }}
							className='drawer-footer'>
							<Group itemsSpacing='8' itemsAlignX='space-between' noWrap>
								<Group.Item>
									<ActionMenuTrigger>
										<Button
											variant='secondary'
											size='large'
											icon={<ChevronDown12 />}
											iconSide='right'>
											More actions
										</Button>
										<ActionMenu>
											<ActionMenuItem
												label='Export order as CSV'
												leadingAccessory={<SquareUp24 />}
												onAction={handleExportAsCsv}
											/>
											<ActionMenuItem
												label='Sync order document'
												leadingAccessory={<ArrowsSync24 />}
												onAction={handleSyncOrder}
											/>
											{currentServicesProviderId ===
												EKHO_SERVICES_PROVIDER_ID && (
												<ActionMenuItem
													label='Open in legacy app'
													leadingAccessory={<ArrowUpRight24 />}
													onAction={handleViewInLegacyApp}
												/>
											)}
										</ActionMenu>
									</ActionMenuTrigger>
								</Group.Item>
								<Group itemsSpacing='8' noWrap>
									<Button
										variant='secondary'
										size='icon'
										icon={<ChevronUp24 />}
										aria-label='Previous order'
										onPress={handlePreviousOrder}
										isDisabled={!navigationState.canGoPrevious}
									/>
									<Button
										variant='secondary'
										size='icon'
										icon={<ChevronDown24 />}
										aria-label='Next order'
										onPress={handleNextOrder}
										isDisabled={!navigationState.canGoNext}
									/>
								</Group>
							</Group>
						</motion.div>
					</Drawer.Footer>
				)}
			</Drawer>
		</DialogTrigger>
	);
};
